import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS, REVIEWS } from "../../../mocks/siteData";

export interface TestimonialsContent {
  eyebrow: string;
  headline: string;
}

interface Props {
  content: TestimonialsContent;
}

export default function TestimonialsSection({ content }: Props) {
  const { eyebrow, headline } = content;

  return (
    <section id="testimonials" className="relative bg-charcoal-dark py-24 md:py-32 overflow-hidden">
      {/* Decorative large quote mark */}
      <div className="absolute top-12 left-8 md:left-16 font-serif text-[200px] text-white/[0.03] leading-none select-none pointer-events-none">
        &ldquo;
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <RevealWrapper>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16">
            <div>
              <span className="section-eyebrow text-gold">{eyebrow}</span>
              <span className="block w-12 h-px bg-gold my-5" />
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight">
                {headline}
              </h2>
            </div>
            {/* Rating badge */}
            <div className="flex items-center gap-4 bg-charcoal border border-white/8 px-6 py-4 flex-shrink-0">
              <div>
                <div className="font-serif text-3xl text-gold font-light leading-none">
                  {BUSINESS.googleRating}
                </div>
                <div className="flex gap-0.5 mt-1">
                  {[1,2,3,4,5].map((i) => (
                    <i key={i} className="ri-star-fill text-gold text-xs" />
                  ))}
                </div>
              </div>
              <div className="w-px h-10 bg-white/15" />
              <div>
                <div className="font-sans text-xs text-white font-medium">
                  {BUSINESS.reviewCount}
                </div>
                <div className="font-sans text-[10px] text-white/35 mt-0.5">
                  Google Reviews
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {REVIEWS.map((review, i) => (
            <RevealWrapper key={i} delay={i * 0.13}>
              <article className="group bg-charcoal border-l-2 border-gold/25 hover:border-gold transition-all duration-400 p-8 flex flex-col h-full">
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-gold text-xs" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="font-serif text-xl font-light text-white/75 leading-relaxed mb-6 italic flex-1">
                  &ldquo;{review.text}&rdquo;
                </blockquote>

                {/* Divider */}
                <div className="w-8 h-px bg-gold/30 mb-5" />

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/15 border border-gold/25 flex-shrink-0">
                    <span className="font-serif text-gold text-base font-light">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-sans text-xs font-medium text-white tracking-wide">
                      {review.name}
                    </div>
                    <div className="font-sans text-[10px] text-white/35 mt-0.5">
                      Google Review &middot; {review.date}
                    </div>
                  </div>
                </div>
              </article>
            </RevealWrapper>
          ))}
        </div>

        {/* Post-testimonials CTA */}
        <RevealWrapper delay={0.4}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-white/8">
            <p className="font-sans text-sm text-white/35 font-light">
              Join hundreds of satisfied clients across Utah County
            </p>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href={BUSINESS.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
              >
                Reserve Your Appointment
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href={BUSINESS.reviewUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn-outline"
              >
                Leave a Review
              </a>
            </div>
          </div>
        </RevealWrapper>
      </div>
    </section>
  );
}
